import React, { useEffect, useContext, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { FlexBox, Spinner } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import PropTypes from 'prop-types';
import useTracking from '../hooks/useTracking';
import useCartLoader from '../hooks/useCartLoader';
import { UserFeatureContext } from './contexts/UserFeatureContext';
import { CartDisplay } from './CartDisplay';
import { cartSelectors } from '../state/cart';
import { ErrorPage } from './ErrorPage';

const DebugTools = lazy(() =>
  import('./debugTooling/DebugTools').then(module => ({
    default: module.DebugTools,
  }))
);

export const CartApplication = ({ tenant, shopperId, locale }) => {
  const isCartLoaded = useCartLoader(tenant, shopperId, locale);
  const tracker = useTracking();
  const { debugEnabled } = useContext(UserFeatureContext);
  const [cartMessages] = useTranslations(['cartMessages']);

  // TODO: We should only be tracking events if the page is loaded by a shopper.
  useEffect(() => {
    if (isCartLoaded) {
      tracker.trackCartPageViewed();
      tracker.trackCartStepViewed();
    }
    // A new error tracking object should not cause this side affect to run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartLoaded]);

  const hasCartLoadError = useSelector(state =>
    cartSelectors.selectIsCartLoadError(state.cart)
  );

  if (hasCartLoadError) {
    return <ErrorPage errorMessage={cartMessages.loadCartError} />;
  }

  if (!isCartLoaded) {
    return (
      <FlexBox justifyContent="center">
        <Spinner
          size="super"
          accessibleText={cartMessages.loading}
          aria-busy="true"
          aria-live="polite"
          role="status"
          my={9}
        />
      </FlexBox>
    );
  }

  return (
    <Suspense
      fallback={
        <Spinner
          accessibleText={cartMessages.loading}
          aria-busy="true"
          aria-live="polite"
          role="status"
        />
      }
    >
      {debugEnabled && <DebugTools />}
      <CartDisplay />
    </Suspense>
  );
};

CartApplication.propTypes = {
  tenant: PropTypes.string.isRequired,
  shopperId: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
};
